import React, { useState, useEffect, useRef } from 'react'

import { useSelector } from 'react-redux'

import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { Action } from '../../types/actionTypes'
import { getActionbyId, updateAction } from '../../redux/actions/actionActions'
import CoutSupplementaireListe from '../CoutSupplementaire/CoutSuppList'
import DelaiListe from '../delai/DelaiList'
import CommentSection from '../comment/CommentList'
import RisqueListByAction from '../risque/RisqueListByAction'
import AuditListByAction from '../audit/AuditListByAction'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import RASCIMatrix from '../RASCI/matriceRASCI'
import SupportDocumentPanel from './SupportDocumentPanel'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { UserRole } from '../../types/keycloak'
import { hasRole } from '../../utils/checkRole'
import { formatTitle } from '../../utils/formatTitle'
import { writeEntityNav } from '../../utils/entityNav'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatCurrency, locale } from '../../utils/formatNumbers'
import {
  TypeActionTranslations,
  priorityLabelsTranslations,
} from '../../utils/constants'
import { selectActeurAction } from '../../redux/actorAction/actorActionSelectors'
import UseAlert from '../../components/common/alert'
import { CheckIcon } from 'lucide-react'
import IncidentListByAction from '../Incident/IncidentlistByAction'
import SecurityCheckFamilyListByAction from '../securityCheckFamily/SecurityCheckFamilyList'
import { useTranslation } from 'react-i18next'
import { fetchDocumentById } from '../../redux/documents/documentAction'
import {
  selectDocument,
  selectDocumentError,
  selectDocumentLoading,
} from '../../redux/documents/documentSelector'
import {
  DetailAccordion,
  DetailButton,
  DetailInfoGrid,
  DetailInfoItem,
  DetailPageHead,
  DetailTabs,
} from '../../components/common/detail'
import {
  ListPageDateCell,
  ListPagePill,
  PRIORITY_PILL,
  STATUS_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import '../../components/common/ListPageTable.css'

export default function ActionDetails() {
  const { t } = useTranslation()
  const username = useSelector(selectUsername)
  const currency = useSelector(selectSpecifications)[0]?.Currency

  const delaisSuppSectionRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState<Record<string, boolean>>({
    info: true,
    description: true,
    activities: false,
    sources: false,
    security: false,
    rasci: false,
    support: true,
  })

  const toggle = (key: string) => () => {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const [activityTab, setActivityTab] = useState('time')
  const [sourceTab, setSourceTab] = useState('audits')

  const [action, setAction] = useState<Action>({
    _id: '',
    ActionId: '',
    ActionName: '',
    Description: '',
    DateDebut: new Date(),
    DateFin: new Date(),
    Status: '',
    Priority: 0,
    Cout: 0,
    CoutTotal: 0,
    DateFinSupp: new Date(),
    MissionId: '',
    TypeAction: '',
    Support: null,
    CreatedBy: '',
  })

  const dispatch: AppDispatch = useAppDispatch()
  const actionsList = useSelector((state: RootState) => state.action.action)
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const document = useSelector(selectDocument)
  const isDocumentLoading = useSelector(selectDocumentLoading)
  const documentError = useSelector(selectDocumentError)
  const [savingSupport, setSavingSupport] = useState(false)

  useEffect(() => {
    if (actionsList?.Support && typeof actionsList.Support === 'string') {
      dispatch(fetchDocumentById(actionsList.Support))
    }
  }, [dispatch, actionsList?.Support])
  useEffect(() => {
    if (id) {
      dispatch(getActionbyId(id))
    }
  }, [dispatch, id])

  const handleRefreshActionData = () => {
    if (id) {
      dispatch(getActionbyId(id))
    }
  }

  useEffect(() => {
    if (actionsList !== null) {
      setAction({
        _id: actionsList._id,
        ActionId: actionsList.ActionId,
        ActionName: actionsList.ActionName,
        Description: actionsList.Description,
        DateDebut: actionsList.DateDebut,
        DateFin: actionsList.DateFin,
        Status: actionsList.Status,
        Priority: actionsList.Priority,
        Cout: actionsList.Cout,
        CoutTotal: actionsList.CoutTotal,
        DateFinSupp: actionsList.DateFinSupp,
        MissionId: actionsList.MissionId,
        TypeAction: actionsList.TypeAction,
        SourceRisque: actionsList.SourceRisque,
        SourceAudit: actionsList.SourceAudit,
        SourceIncident: actionsList.SourceIncident,
        Support: actionsList.Support,
        CreatedBy: actionsList.CreatedBy,
      })
      if (actionsList._id) {
        writeEntityNav({
          taskId: actionsList._id,
          taskLabel: formatTitle(actionsList.ActionName || ''),
          missionId: actionsList.MissionId || undefined,
          missionLabel: actionsList.MissionName
            ? formatTitle(actionsList.MissionName)
            : undefined,
          projectId: actionsList.ProjetId || undefined,
          projectLabel: actionsList.ProjetName
            ? formatTitle(actionsList.ProjetName)
            : undefined,
        })
      }
    }
  }, [actionsList])

  const handleValidateAction = async () => {
    try {
      const updatedAction = {
        ...action,
        Status: 'Terminé',
      }

      await dispatch(
        updateAction({
          updatedAction,
          _id: action._id as string,
        })
      ).unwrap()

      setAlert({
        open: true,
        message: t('alerts.actionUpdate.success'),
        severity: 'success',
      })

      setAction(updatedAction)
      handleRefreshActionData()
    } catch (error) {
      const translatedMessage =
        (error as string) || t('alerts.actionUpdate.error')
      setAlert({
        open: true,
        message: translatedMessage,
        severity: 'error',
      })
      console.error('Failed to update action:', error)
    }
  }
  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    })
  }
  const roles = useSelector((state: RootState) => state.auth.roles)
  const actor = useSelector(selectActeurAction)

  const title = formatTitle(action?.ActionName as string) || ''
  const priority = Number(action.Priority) || 0
  const priorityStyle = PRIORITY_PILL[priority] ?? PRIORITY_PILL[4]
  const statusStyle = STATUS_PILL[action.Status || ''] ?? {
    bg: '#EAF0F0',
    color: '#5F7484',
  }

  const canValidate =
    username?.toLocaleLowerCase() ===
      actor?.RASCI?.Approbateur[0]?.toLocaleLowerCase() &&
    action.Status !== 'Terminé'

  const canUpdate =
    username?.toLocaleLowerCase() ===
      action.CreatedBy?.toLocaleLowerCase() ||
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.ADMIN) ||
    hasRole(roles, UserRole.SSI_TEAM)

  const supportMissing =
    !action.Support ||
    !!documentError ||
    (!isDocumentLoading && !!action.Support && !document?.originalName)

  const handleSaveSupport = async (file: File) => {
    if (!action._id) return
    setSavingSupport(true)
    try {
      const updated = await dispatch(
        updateAction({
          updatedAction: { ...action, Support: file },
          _id: action._id,
        })
      ).unwrap()

      setAlert({
        open: true,
        message: t('alerts.actionUpdate.success'),
        severity: 'success',
      })

      if (updated?.Support && typeof updated.Support === 'string') {
        setAction((prev) => ({ ...prev, Support: updated.Support }))
        await dispatch(fetchDocumentById(updated.Support))
      } else {
        handleRefreshActionData()
      }
    } catch (error) {
      setAlert({
        open: true,
        message: (error as string) || t('alerts.actionUpdate.error'),
        severity: 'error',
      })
    } finally {
      setSavingSupport(false)
    }
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <div className="detail-page">
        <DetailPageHead
          eyebrow={t('taskDetailsPage.eyebrow', {
            defaultValue: 'Management · Tasks',
          })}
          title={title}
          subtitle={t('taskDetailsPage.subtitle', {
            defaultValue:
              'Task details, activities, sources, and ownership in one place.',
          })}
          actions={
            <>
              {canValidate ? (
                <DetailButton onClick={handleValidateAction}>
                  <CheckIcon size={16} />
                  {t('buttons.validateAction')}
                </DetailButton>
              ) : null}
              {canUpdate ? (
                <DetailButton
                  onClick={() => {
                    navigate(`/action/${action._id}`)
                  }}
                >
                  <DriveFileRenameOutlineOutlined sx={{ fontSize: 16 }} />
                  {t('buttons.updateTask')}
                </DetailButton>
              ) : null}
            </>
          }
        />

        <DetailAccordion
          title={t('taskDetailsPage.accordion.information')}
          open={open.info}
          onToggle={toggle('info')}
        >
          <DetailInfoGrid>
            <DetailInfoItem
              label={t('taskDetailsPage.accordion.type')}
              value={
                action.TypeAction
                  ? t(
                      TypeActionTranslations[action.TypeAction as string] ||
                        action.TypeAction
                    )
                  : '—'
              }
            />
            <DetailInfoItem
              label={t('taskDetailsPage.accordion.priority')}
              value={
                <ListPagePill
                  label={
                    t(`priority.${priority}`, {
                      defaultValue:
                        priorityLabelsTranslations[priority] ||
                        priorityStyle.label,
                    }) || priorityStyle.label
                  }
                  bg={priorityStyle.bg}
                  color={priorityStyle.color}
                />
              }
            />
            <DetailInfoItem
              label={t('taskDetailsPage.accordion.startDate')}
              mono
              value={
                <ListPageDateCell value={action.DateDebut} locale={locale} />
              }
            />
            <DetailInfoItem
              label={t('taskDetailsPage.accordion.endDate')}
              mono
              value={
                <ListPageDateCell value={action.DateFin} locale={locale} />
              }
            />
            <DetailInfoItem
              label={t(
                'taskDetailsPage.activitiesAccordion.tabs.additionalTime'
              )}
              mono
              value={
                <ListPageDateCell
                  value={action.DateFinSupp}
                  locale={locale}
                />
              }
            />
            <DetailInfoItem
              label={t('taskDetailsPage.accordion.initialCost')}
              mono
              value={formatCurrency(action.Cout ?? 0, currency, {
                locale,
                useIntl: true,
              })}
            />
            <DetailInfoItem
              label={t('taskDetailsPage.accordion.totalCost')}
              mono
              value={formatCurrency(action.CoutTotal ?? 0, currency, {
                locale,
                useIntl: true,
              })}
            />
            <DetailInfoItem
              label={t('taskDetailsPage.accordion.creator')}
              value={action.CreatedBy || '—'}
            />
            <DetailInfoItem
              label={t('taskDetailsPage.accordion.status')}
              value={
                action.Status ? (
                  <ListPagePill
                    label={t(action.Status, {
                      defaultValue: action.Status,
                    })}
                    bg={statusStyle.bg}
                    color={statusStyle.color}
                  />
                ) : (
                  '—'
                )
              }
            />
          </DetailInfoGrid>
        </DetailAccordion>

        <DetailAccordion
          title={t('taskDetailsPage.accordionSummary.description')}
          open={open.description}
          onToggle={toggle('description')}
        >
          <div className="detail-acc-inner">
            <ListPageDescription value={action.Description} />
          </div>
        </DetailAccordion>

        <DetailAccordion
          title={t('taskDetailsPage.accordionSummary.activities')}
          open={open.activities}
          onToggle={toggle('activities')}
        >
          <div ref={delaisSuppSectionRef} className="detail-acc-inner">
            <DetailTabs
              activeId={activityTab}
              onChange={setActivityTab}
              tabs={[
                {
                  id: 'time',
                  label: t(
                    'taskDetailsPage.activitiesAccordion.tabs.additionalTime'
                  ),
                },
                {
                  id: 'cost',
                  label: t(
                    'taskDetailsPage.activitiesAccordion.tabs.additionalCost'
                  ),
                },
                {
                  id: 'comments',
                  label: t(
                    'taskDetailsPage.activitiesAccordion.tabs.comment'
                  ),
                },
              ]}
            >
              {activityTab === 'time' ? (
                <DelaiListe
                  idaction={action._id as string}
                  delaisSuppSectionRef={delaisSuppSectionRef}
                  onDataChange={handleRefreshActionData}
                  actionData={action}
                />
              ) : null}
              {activityTab === 'cost' ? (
                <CoutSupplementaireListe
                  idaction={action._id as string}
                  onDataChange={handleRefreshActionData}
                />
              ) : null}
              {activityTab === 'comments' ? (
                <CommentSection idaction={action._id as string} />
              ) : null}
            </DetailTabs>
          </div>
        </DetailAccordion>

        <DetailAccordion
          title={t('taskDetailsPage.accordionSummary.sources')}
          open={open.sources}
          onToggle={toggle('sources')}
        >
          <div className="detail-acc-inner">
            <DetailTabs
              activeId={sourceTab}
              onChange={setSourceTab}
              tabs={[
                {
                  id: 'audits',
                  label: t('Audits', { defaultValue: 'Audits' }),
                },
                { id: 'risks', label: t('risks') },
                {
                  id: 'incidents',
                  label: t('Incidents', { defaultValue: 'Incidents' }),
                },
              ]}
            >
              {sourceTab === 'audits' ? (
                <AuditListByAction
                  ActionId={action._id as string}
                  MissionId={action.MissionId || ''}
                />
              ) : null}
              {sourceTab === 'risks' ? (
                <RisqueListByAction
                  ActionId={action._id as string}
                  MissionId={action.MissionId || ''}
                />
              ) : null}
              {sourceTab === 'incidents' ? (
                <IncidentListByAction
                  ActionId={action._id as string}
                  MissionId={action.MissionId || ''}
                />
              ) : null}
            </DetailTabs>
          </div>
        </DetailAccordion>

        <DetailAccordion
          title={t('taskDetailsPage.accordionSummary.securityCheckFamily')}
          open={open.security}
          onToggle={toggle('security')}
        >
          <div className="detail-acc-inner">
            <SecurityCheckFamilyListByAction
              ActionId={action._id as string}
              MissionId={action.MissionId || ''}
            />
          </div>
        </DetailAccordion>

        <DetailAccordion
          title={t('taskDetailsPage.accordionSummary.matriceRasci')}
          open={open.rasci}
          onToggle={toggle('rasci')}
        >
          <div className="detail-acc-inner">
            <RASCIMatrix action={action} />
          </div>
        </DetailAccordion>

        <DetailAccordion
          title={t('taskDetailsPage.accordionSummary.supportDocument')}
          open={open.support}
          onToggle={toggle('support')}
        >
          <div className="detail-acc-inner">
            <SupportDocumentPanel
              hasSupportId={!!action.Support}
              document={document}
              loading={isDocumentLoading}
              missingOrBroken={supportMissing}
              canManage={canUpdate}
              saving={savingSupport}
              onSaveFile={handleSaveSupport}
            />
          </div>
        </DetailAccordion>
      </div>
    </>
  )
}
